import { useEffect, useState } from 'react'
import { Button, Skeleton } from '@cannect/new-components/atoms'
import { formatToBRL } from '@cannect/utils/formatters'
import { useRemoveSubscriptionItem, useSubscriptionPreview } from '@cannect/services/resources/recurrency/hooks'
import { formatApiDate } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail, SubscriptionItem } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { activeItems } from '../hooks/useEditDraft'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.removeItem

type Props = {
  detail: SubscriptionDetail
  item: SubscriptionItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onCancelSubscription: () => void
}

export const RemoveItemDialog = ({ detail, item, open, onOpenChange, onCancelSubscription }: Props) => {
  const [lastItem, setLastItem] = useState(false)
  const onlyOne = activeItems(detail).length <= 1
  const showLastItem = onlyOne || lastItem

  useEffect(() => {
    if (!open) setLastItem(false)
  }, [open])

  const preview = useSubscriptionPreview(
    detail.id,
    open && item && !showLastItem ? { removeProductId: item.productId } : null
  )

  const action = useSubscriptionAction({
    detail,
    mutation: useRemoveSubscriptionItem(detail.id),
    event: 'recurrency_edit',
    eventPayload: () => ({ change: 'remove_item' }),
    onErrorCode: { LAST_ITEM: () => setLastItem(true) }
  })

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title(item?.name || 'produto')}
      description={showLastItem ? copy.lastItem : undefined}
      footer={
        <>
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          {showLastItem ? (
            <Button className="bg-red-700 text-white hover:bg-red-700/90" onClick={onCancelSubscription}>
              {subscriptionsCopy.buttons.cancel}
            </Button>
          ) : (
            <Button
              isLoading={action.isPending}
              disabled={!item}
              onClick={async () => {
                if (item && (await action.run(item.productId))) onOpenChange(false)
              }}>
              {copy.confirm}
            </Button>
          )}
        </>
      }>
      {showLastItem ? (
        <p className="m-0 text-sm text-neutral-700">{copy.lastItemHelp}</p>
      ) : (
        <div aria-live="polite" className="rounded-lg bg-neutral-50 p-3 text-sm">
          {preview.isFetching && !preview.data && <Skeleton className="h-10 w-full" />}
          {preview.data && (
            <dl className="m-0 flex flex-col gap-1">
              <div className="flex justify-between">
                <dt>{subscriptionsCopy.preview.newTotal}</dt>
                <dd className="font-semibold">{formatToBRL(preview.data.estimatedAmount ?? undefined)}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{subscriptionsCopy.preview.nextCharge}</dt>
                <dd>{formatApiDate(preview.data.nextChargeAt)}</dd>
              </div>
            </dl>
          )}
        </div>
      )}
    </DialogShell>
  )
}
